import type { OnboardingState } from '../api/endpoints.js';

/**
 * The onboarding journey, transcribed from the mobile app's flow
 * (onboarding-flow-and-api.md) onto this backend's own step machine.
 *
 * SKIPS ARE THE SERVER'S NOW. They used to live here because
 * profile.onboarding_skips had no write route, and that meant a skip existed in
 * exactly one browser: signing out lost it, and a second device never had it. A
 * tester hit the consequence — sign out, sign in, and the wizard started over.
 * POST /v1/onboarding/skips records them, `onboarding.skipped_steps` reads them
 * back, and both halves of the decision are now the same server state on every
 * device.
 *
 * What is still local is only what the server deliberately does not model:
 * `purpose` (the client's branch choice), `familyComplete` (a client-side loop
 * with no server step) and `checkpoint` (position inside the pre-write block).
 * None of them may override server progress — see resumeStep.
 */
export type FlowStep =
  | 'purpose'
  | 'info'
  | 'assessment'
  | 'quiz'
  | 'calculating'
  | 'score'
  | 'plans'
  | 'skip-plan'
  | 'profile'
  | 'face-intro'
  | 'consent'
  | 'selfie'
  | 'profile-extra'
  | 'photos'
  | 'notifications'
  | 'family-choice'
  | 'family-member'
  | 'family-photos';

export type Purpose = 'just_me' | 'my_family';

/**
 * What the flow remembers about this browser — and ONLY the things the server
 * does not model. The five skip flags that used to live here (quiz, plan,
 * profile_extra, photos, notifications) are now server skips, because a skip
 * kept here is progress that dies with a sign-out.
 */
export interface FlowFlags {
  /** `onboardingPurpose` — chosen at the branch point, read at the very end. */
  purpose: Purpose | null;
  /** `onboardingComplete` — set by the family-choice screen's finish actions. */
  familyComplete: boolean;
  /** `onboardingCheckpoint` — position within the local quiz block only. */
  checkpoint: FlowStep | null;
}

export const BLANK_FLAGS: FlowFlags = {
  purpose: null,
  familyComplete: false,
  checkpoint: null,
};

/**
 * The screens before the first server write. Only these are worth a checkpoint:
 * everything after the quiz is derivable from the server flags, and the mobile
 * app deliberately refuses to resume into the score screen (stale checkpoint).
 */
const CHECKPOINT_STEPS: readonly FlowStep[] = ['purpose', 'info', 'assessment', 'quiz'];

export function isCheckpointStep(step: FlowStep): boolean {
  return CHECKPOINT_STEPS.includes(step);
}

/**
 * Where a cold entry lands. 'done' means the dashboard.
 *
 * The mobile app's OTP router in one function: server flags first, local skips
 * second, the checkpoint only within the pre-write block. `next_step === null`
 * is the server saying onboarding is finished; the one exception is the family
 * branch, which is a purely local loop the server has no step for. And 'email'
 * never blocks — verification is a dashboard recommended action, not a wall,
 * which is why the booleans are walked here instead of trusting `next_step`
 * alone (a skipped plan would pin `next_step` at 'plan' forever).
 */
/**
 * Nothing has happened yet on the server — no step complete, none skipped.
 *
 * This is what qualifies someone for the purpose screen, and it is checked
 * against every field rather than `next_step === 'quiz'`: the steps are not
 * strictly ordered in practice (a person can complete their profile while the
 * quiz is still owed), so next_step alone would call a mid-flow user fresh.
 */
function nothingStarted(onboarding: OnboardingState): boolean {
  return (
    onboarding.skipped_steps.length === 0 &&
    !onboarding.quiz_completed &&
    !onboarding.seat_claimed &&
    !onboarding.profile_complete &&
    !onboarding.consent_signed &&
    !onboarding.enrolled &&
    !onboarding.email_verified &&
    !onboarding.photos_uploaded &&
    !onboarding.notifications_enabled
  );
}

export function resumeStep(
  onboarding: OnboardingState,
  hasProfileExtras: boolean,
  flags: FlowFlags,
): FlowStep | 'done' {
  const skipped = (step: string): boolean => onboarding.skipped_steps.includes(step);
  const familyPending = flags.purpose === 'my_family' && !flags.familyComplete;
  if (onboarding.next_step === null) return familyPending ? 'family-choice' : 'done';
  // ONLY for someone who has genuinely done nothing. `purpose` is the one input
  // with no server home, so a sign-out leaves it null for people the server
  // knows are most of the way through — and returning 'purpose' here without
  // consulting the server is what restarted a tester's whole flow. Anyone with
  // progress keeps it and skips this screen; the cost is that they do not
  // re-enter the family loop, which is reachable from the household screen.
  if (flags.purpose === null && nothingStarted(onboarding)) return 'purpose';
  if (!onboarding.quiz_completed && !skipped('quiz')) {
    if (flags.checkpoint !== null && CHECKPOINT_STEPS.includes(flags.checkpoint)) {
      return flags.checkpoint;
    }
    return 'info';
  }
  if (!onboarding.seat_claimed && !skipped('plan')) return 'plans';
  if (!onboarding.profile_complete) return 'profile';
  if (!onboarding.consent_signed) return 'face-intro';
  if (!onboarding.enrolled) return 'selfie';
  if (!hasProfileExtras && !skipped('profile_extra')) return 'profile-extra';
  if (!onboarding.photos_uploaded && !skipped('photos')) return 'photos';
  if (!onboarding.notifications_enabled && !skipped('notifications')) return 'notifications';
  if (familyPending) return 'family-choice';
  return 'done';
}

/**
 * Monotonic, unlike the mobile app's — its hard-coded per-screen values regress
 * five times (flow doc §15.3), which the doc itself flags as a defect. The
 * ORDER is the doc's; only the arithmetic is repaired.
 */
const PROGRESS: Record<FlowStep, number> = {
  purpose: 8,
  info: 16,
  assessment: 24,
  quiz: 30,
  calculating: 48,
  score: 52,
  plans: 60,
  'skip-plan': 64,
  profile: 70,
  'face-intro': 74,
  consent: 80,
  selfie: 86,
  'profile-extra': 90,
  photos: 94,
  notifications: 97,
  'family-choice': 97,
  'family-member': 98,
  'family-photos': 99,
};

export function progressOf(step: FlowStep): number {
  return PROGRESS[step];
}

export function isFlowStep(value: string): value is FlowStep {
  return value in PROGRESS;
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

/**
 * localStorage, not sessionStorage: these are UX flags (which screens were
 * walked past), the same class of thing the mobile app keeps in AsyncStorage.
 * Nothing here is a credential — tokens stay in api/client.ts's session rules.
 */
const STORAGE_KEY = 'imageshield.flow';

export function readFlags(): FlowFlags {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...BLANK_FLAGS };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...BLANK_FLAGS };
    return { ...BLANK_FLAGS, ...(parsed as Partial<FlowFlags>) };
  } catch {
    return { ...BLANK_FLAGS };
  }
}

export function writeFlags(patch: Partial<FlowFlags>): FlowFlags {
  const next = { ...readFlags(), ...patch };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Blocked storage: the flow still works for this page's lifetime.
  }
  return next;
}

/** Sign-out and account deletion both start the next user from a clean flow. */
export function clearFlags(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear is fine.
  }
}
